import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Store } from '../../../../redux';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  useTheme,
  InputAdornment,
} from '@mui/material';
import { Texts } from '../../../../shared/types';
import { botImage } from '../../../../assets';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHttpMutation } from '../../../../api';
import { API_URLS } from '../../consts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAlert } from '../../../../shared/components';
import { Cpu } from 'lucide-react';

export declare type InviteBotsInput = {
  numberOfBots: number;
};

type InviteBotsText = Texts<'subText' | 'submit' | 'warning'>;
export interface InviteBotsProps {
  texts?: InviteBotsText;
}

const defaultText: InviteBotsText = {
  subText: `Meet Omega, the bot. It's a great way to play when you don't have enough players. 
  Select number of bots and start playing.`,
  submit: 'Submit',
  warning: 'You need to be in a room to invite bots.',
};

export const InviteBots: FC<InviteBotsProps> = ({ texts = defaultText }) => {
  const theme = useTheme();
  const {
    gameInfo: { room },
    players,
    isInRoom,
  } = useSelector((state: Store) => ({
    ...state.inPlayReducer,
    isInRoom: !!state.userReducer.isInRoom,
  }));

  const [params] = useSearchParams();
  const playersInParams = params.get('noOfPlayers');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<InviteBotsInput>({
    mode: 'onChange',
    defaultValues: {
      numberOfBots: undefined,
    },
  });

  const botCount = watch('numberOfBots');

  const { mutate, isLoading } = useHttpMutation<unknown, InviteBotsInput>(
    API_URLS.INVITE_BOTS,
    'POST'
  );

  const numberOfPlayers = useMemo(() => {
    if (playersInParams) {
      const parsed = parseInt(playersInParams, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return room?.numberOfPlayers || 0;
  }, [playersInParams, room?.numberOfPlayers]);

  const maxBotsAllowed = useMemo(
    () => (numberOfPlayers > 0 ? numberOfPlayers - (players.length || 1) : 1),
    [players.length, numberOfPlayers]
  );

  const { addAlert } = useAlert();
  const nav = useNavigate();

  const onSubmit: SubmitHandler<InviteBotsInput> = data => {
    mutate(
      {
        numberOfBots: Number(data.numberOfBots),
      },
      {
        onError: () => {
          addAlert({
            message: 'Error inviting bots',
            type: 'error',
          });
        },
        onSuccess: () => {
          nav('/in-play');
        },
      }
    );
  };

  return (
    <Box className="flex flex-col items-center pt-4 gap-1 px-4 w-full max-w-lg mx-auto flex-1 h-full min-h-0 overflow-y-auto">
      {/* Header */}
      <Box className="text-center mb-0.5">
        <Box className="flex items-center justify-center gap-2 mb-1.5 sm:gap-1.5">
          <Cpu size={28} style={{ color: theme.palette.primary.main }} />
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{ fontSize: '1.75rem', color: 'text.primary' }}
            className="text-[1.2rem] sm:text-[1rem]"
          >
            Meet Omega
          </Typography>
        </Box>
      </Box>

      {/* Bot Card */}
      <Paper
        className="w-full max-w-[480px] md:max-w-[95%] sm:max-w-[95%] p-2.5 md:p-2 sm:p-1.5 gap-1 border-2 rounded-2xl flex flex-col items-center"
        sx={{
          borderColor: theme.palette.divider,
          background: theme.palette.background.paper,
          boxShadow: 'var(--bh-shadow-sm)',
        }}
      >
        {/* Bot Image + Message Container */}
        <Box className="flex gap-2.5 sm:gap-1.5 items-start w-full">
          {/* Bot Image */}
          <Box className="w-20 h-20 sm:w-16 sm:h-16 flex-shrink-0 flex items-center justify-center">
            <img
              src={botImage}
              alt="Omega Bot"
              className="w-full h-full object-contain"
            />
          </Box>

          {/* Description Text */}
          <Typography
            variant="body2"
            color="text.secondary"
            className="text-[0.75rem] leading-[1.35] sm:text-[0.65rem] sm:leading-[1.25] pt-2"
          >
            {texts.subText}
          </Typography>
        </Box>

        {/* Status Message */}
        {!isInRoom && (
          <Box
            className="w-full p-2 rounded-lg text-[0.7rem] sm:text-[0.65rem] font-semibold text-center"
            sx={{
              color: theme.palette.warning.main,
            }}
          >
            ⚠️ {texts.warning}
          </Box>
        )}

        {/* Bot Count Label */}
        <Box className="w-full mb-0.25 text-center">
          <Typography
            variant="caption"
            className="text-[0.6rem] sm:text-[0.55rem] font-bold tracking-widest uppercase"
            sx={{
              color: theme.palette.primary.main,
            }}
          >
            🤖 Select Number of Bots
          </Typography>
        </Box>

        {/* TextField */}
        <TextField
          type="number"
          fullWidth
          {...register('numberOfBots', {
            required: 'Number of bots is required',
            min: { value: 1, message: 'Minimum 1 bot required' },
            max: {
              value: maxBotsAllowed,
              message: `Maximum ${maxBotsAllowed} bots allowed`,
            },
          })}
          error={!!errors.numberOfBots}
          disabled={!isInRoom}
          placeholder="Enter number of bots"
          InputProps={{
            endAdornment: botCount && !isNaN(botCount) && botCount > 0 && (
              <InputAdornment position="end">
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                  }}
                >
                  {botCount} {botCount === 1 ? 'bot' : 'bots'}
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.9rem',
              fontWeight: 600,
              py: 0.9,
              borderRadius: 2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)',
              '@media (max-width: 640px)': {
                fontSize: '0.85rem',
                py: 0.7,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: 2,
            },
            '&.Mui-disabled': {
              opacity: 0.6,
            },
          }}
          helperText={
            <Box
              className="text-[0.65rem] sm:text-[0.6rem] font-semibold mt-0.25"
              sx={{
                color: errors?.numberOfBots?.message
                  ? theme.palette.error.main
                  : theme.palette.success.main,
              }}
            >
              {errors.numberOfBots?.message ||
                (isInRoom && `Max bots allowed: ${maxBotsAllowed}`)}
            </Box>
          }
        />

        {/* Bot Count Indicators */}
        {botCount &&
          !isNaN(botCount) &&
          botCount >= 1 &&
          botCount <= maxBotsAllowed && (
            <Box className="flex gap-0.3 justify-center flex-wrap mt-0.5 w-full">
              {Array.from({ length: botCount }).map((_, i) => (
                <Box
                  key={i}
                  className="w-5 h-5 rounded-md flex items-center justify-center text-[0.6rem] font-bold"
                  sx={{
                    background: 'var(--bh-felt)',
                    color: 'var(--bh-ivory)',
                    boxShadow: 'inset 0 0 0 2px rgba(247, 240, 225, 0.25)',
                    animation: `scaleIn 0.3s ease-out ${i * 0.05}s both`,
                    '@keyframes scaleIn': {
                      '0%': {
                        transform: 'scale(0)',
                        opacity: 0,
                      },
                      '100%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {i + 1}
                </Box>
              ))}
            </Box>
          )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={
            !isInRoom ||
            isLoading ||
            !!errors.numberOfBots ||
            !botCount ||
            isNaN(botCount)
          }
          onClick={handleSubmit(onSubmit)}
          className="mt-1 sm:mt-0.5"
        >
          {isLoading ? 'Seating bots…' : texts.submit}
        </Button>
      </Paper>
    </Box>
  );
};
